import { useEffect, useLayoutEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Merlin, useWizard, Wizard } from '@zillow/zrs-rental-applications';
import EnterYourNameStep from './EnterYourNameStep';
import { Flex, Page, PageContent, Spinner } from '@zillow/constellation';
import EnterYourAddressStep from './EnterYourAddressStep';
import InviteARenterStep from './InviteARenterStep';
import { ZTSS_ENTER_YOUR_ADDRESS, ZTSS_ENTER_YOUR_NAME } from '../../../constants/paths';
import useGetNames from '../../../utils/apis/useGetNames';
import { getClickstreamEventsByStepId, WIZARD_STEPS } from '../TenantScreeningServicesPage.utils';
import { useListingUser } from '../../../hooks/useListingUser';

/**
 * Avoiding rendering the actual component until necessary. This avoids hooks being fired off before the step is being rendered
 *
 * @param {Object} props
 * @param {String} props.id
 * @param {String} props.label
 * @param {Function} props.render
 */
function RenderStep({ id, label, render }) {
  const { currentStep } = useWizard();
  if (currentStep !== id) {
    return <Wizard.Step id={id} label={label} render={() => null} />;
  }
  return render();
}

function useRedirectBasedOnName() {
  const history = useHistory();
  const { currentStep } = useParams();
  const { isFetched, refetch: getNames } = useGetNames({
    select(responseData) {
      return responseData.data.response.profileInfoDtos[0];
    },
    enabled: false,
  });
  useLayoutEffect(() => {
    async function checkNames() {
      const { data } = await getNames();
      if (!data?.firstName || (!data?.lastName && currentStep !== 'enter-your-name')) {
        history.replace(ZTSS_ENTER_YOUR_NAME);
      }
      if (data?.firstName && data?.lastName && currentStep === 'enter-your-name') {
        history.replace(ZTSS_ENTER_YOUR_ADDRESS);
      }
    }
    if (!isFetched) {
      checkNames();
    }
  }, [currentStep, history, getNames, isFetched]);
  return isFetched;
}

export default function ListingWizardPage() {
  const { currentStep } = useParams();
  const isFetched = useRedirectBasedOnName();
  const clickstreamEvents = getClickstreamEventsByStepId(currentStep);
  const { user, listing } = useListingUser();

  useEffect(() => {
    // these events intentionally use an object param since not all
    //  of the events returned use user AND listing
    clickstreamEvents?.viewEvent({ user, listing });
  }, [currentStep]);

  if (!isFetched) {
    return (
      <Page>
        <PageContent>
          <Flex display="flex" justifyContent="center" alignItems="center">
            <Spinner size="lg" />
          </Flex>
        </PageContent>
      </Page>
    );
  }
  return (
    <Page>
      <PageContent>
        <Merlin
          title="Screen a tenant"
          currentStep={currentStep}
          renderTitle={null}
          renderSaveAndExitButton={null}
          renderProgressBar={null}
          shouldExitAfterLastStep={false}
          onRequestNext={() => clickstreamEvents?.continueEvent({ user, listing })}
        >
          <RenderStep
            id={WIZARD_STEPS.ENTER_YOUR_NAME}
            label="Your name"
            render={() => <EnterYourNameStep id="enter-your-name" label="Your name" />}
          />
          <RenderStep
            id={WIZARD_STEPS.ENTER_YOUR_ADDRESS}
            label="Rental address"
            render={() => <EnterYourAddressStep id="enter-your-address" label="Rental address" />}
          />
          <RenderStep
            id={WIZARD_STEPS.INVITE_A_RENTER}
            label="Renter contact info"
            render={() => <InviteARenterStep id="invite-a-renter" label="Renter contact info" />}
          />
        </Merlin>
      </PageContent>
    </Page>
  );
}
